import { useContext, useMemo, type ReactNode } from 'react';
import { useLocation } from 'react-router-dom';
import { ChatSidePanelHost } from '@trinity/chat-components/components/BrandInsights/SidePanelHost.js';
import { ChatPageContextDataContext } from '@trinity/chat-components/components/Chat/context.js';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useAppReadyState } from '@/containers/AppReadyState';
import { canShowChatWidget, chatRouteAdapter, resolveChatRoutePolicy } from './side-panel-chat-policy';

type Props = {
  children: ReactNode;
};

/**
 * The chat for the routed page: a side panel next to it on the routes that have one, the lightbulb
 * and modal elsewhere. Wraps the routed content, so it stays mounted (and keeps the chat session)
 * as the user moves between pages.
 */
export const ChatSidePanelContainer = ({ children }: Props) => {
  const { pathname, search } = useLocation();
  const apiClient = useChatApiClient();
  const app = useAppReadyState();
  const pageContext = useContext(ChatPageContextDataContext);

  const policy = useMemo(() => resolveChatRoutePolicy(pathname, search), [pathname, search]);
  const canMountChat = app.initialized && app.authenticated;
  const canShowWidget = canShowChatWidget({ policy, hasPageContext: !!pageContext, canMountChat });

  return (
    <ChatSidePanelHost
      policy={policy}
      routeAdapter={chatRouteAdapter}
      canShowWidget={canShowWidget}
      canMountChat={canMountChat}
      // These pages scroll with the window and use `position: sticky` against the viewport.
      mainAreaScrolls={false}
      fetchProjectSegments={apiClient.projects.fetchProjectSegments}
    >
      {children}
    </ChatSidePanelHost>
  );
};
